<template>
  <section v-if="filters">
    <div
      v-if="filters.isCleareable"
      class="flex justify-between items-center p-4"
    >
      <div class="flex items-center gap-3">
        <Button
          v-if="filters.isCleareable"
          label="Limpar"
          icon="pi pi-times"
          class="btn-outline p-button-sm"
          @click="onClearFilter()"
        />
      </div>
      <div class="hidden md:flex"></div>
      <div class="flex md:hidden">
        <Button class="btn-primary p-button-sm" icon="pi pi-plus" />
      </div>
    </div>
  </section>

  <DataTable :value="slotData" :loading="loading" showGridlines>
    <template #empty>
      <p class="text-gray-400 text-center p-3">{{ "Dados não encontrados" }}</p>
    </template>

    <Column
      v-for="(col, i) of columns"
      :field="col.value"
      :header="col.name"
      :key="i"
      class="table-style"
      headerClass="custom-header"
    >
      <template #header>
        <div
          v-if="col?.image"
          class="md:flex items-center justify-center gap-3 hidden bg-white p-1 rounded-md"
          :title="col.name"
        >
          <v-icon
            v-if="col.iconName"
            :name="col.iconName"
            class="text-h-primary-500"
          ></v-icon>
          <img
            v-else
            :src="col.imageName"
            alt="coluna"
            class="w-[80%] min-w[100px]"
          />
        </div>
        <div v-else class="md:flex items-center gap-3 hidden">
          <span>{{ col.name }}</span>
          <i
            v-if="filters && col.filter && !col?.order"
            :class="`pi pi-filter text-white cursor-pointer hover:scale-125`"
            @click="onToggleFilter($event, col)"
          ></i>

          <i
            v-if="filters && col.filter && col?.order"
            :class="`pi pi-sort-alt text-white cursor-pointer hover:scale-125`"
            @click="onSortFilter(col)"
          ></i>
        </div>
      </template>

      <template #body="slotProps">
        <div v-if="Array.isArray(slotProps.data[col.value])">
          <div v-for="(item, i) in slotProps.data[col.value]" :key="i">
            <div v-if="item?.name" class="flex justify-center items-center">
              <p v-if="!item?.class">
                {{ item ?? " - " }}
              </p>

              <p
                v-else
                :class="`${item?.class} py-1 px-2 rounded-xl text-xs  w-100 mt-1`"
              >
                {{ item?.name ?? " - " }}
              </p>
            </div>
            <p v-else>
              {{ item ?? " - " }}
            </p>
          </div>
        </div>
        <div v-else>
          <div
            v-if="slotProps.data[col.value]?.name"
            class="flex justify-center items-center"
          >
            <p v-if="!slotProps.data[col.value]?.class">
              <v-icon
                v-if="slotProps.data[col.value]?.icon"
                :name="slotProps.data[col.value]?.icon"
                :scale="slotProps.data[col.value]?.iconSize ?? 0.6"
              />
              {{ slotProps.data[col.value]?.name ?? " - " }}
            </p>

            <p
              v-else
              :class="`${
                slotProps.data[col.value]?.class
              } py-1 px-2 rounded-md text-xs  w-100`"
            >
              <v-icon
                v-if="slotProps.data[col.value]?.icon"
                :name="slotProps.data[col.value]?.icon"
                :scale="slotProps.data[col.value]?.iconSize ?? 0.6"
              />
              {{ slotProps.data[col.value]?.name ?? " - " }}
            </p>
          </div>
          <p
            v-else
            class="line-clamp-2"
            :title="
              slotProps.data[col.completeValue] ??
              slotProps.data[col.value] ??
              '-'
            "
          >
            {{ slotProps.data[col.value] ?? " - " }}
          </p>
        </div>
      </template>
    </Column>

    <Column v-if="hasActions" :header="actionsTitle" style="max-width: 16rem">
      <template #body="slotProps">
        <div
          v-if="actions?.length !== 0"
          class="flex justify-center items-center gap-4"
        >
          <div v-for="(action, i) in actions" :key="i">
            <Button
              :class="`flex items-center gap-2 transition-all p-button-sm ${
                action.style
              } cursor-pointer ${action.icon ? 'hover:scale-110' : ''}`"
              v-tooltip="`${action.tooltip ?? ''}`"
              @click="onEvent(slotProps.data, action.type)"
              :disabled="action.disabled"
            >
              <span v-if="action.title">{{ action.title }}</span>
              <v-icon v-if="action.icon" :name="action.icon" />
            </Button>
          </div>

          <div v-if="hasStatus">
            <Button
              v-if="
                slotProps.data.status?.value == 'INACTIVATED' ||
                slotProps.data.status?.value == 'INACTIVE'
              "
              class="btn-simple"
              v-tooltip="'Ativar'"
            >
              <v-icon
                name="hi-solid-minus-circle"
                class="text-gray-400 cursor-pointer hover:scale-125"
                @click="onChangeStatus(slotProps.data)"
              />
            </Button>
            <Button
              v-if="
                slotProps.data.status?.value == 'ACTIVATED' ||
                slotProps.data.status?.value == 'ACTIVE'
              "
              class="btn-simple"
              v-tooltip="'Desativar'"
            >
              <v-icon
                name="hi-solid-minus-circle"
                class="text-h-error-400 cursor-pointer hover:scale-125"
                @click="onChangeStatus(slotProps.data)"
              />
            </Button>
            <Button
              v-if="slotProps.data.status?.value == 'BLOCKED'"
              class="btn-simple"
              v-tooltip="'Desbloquear'"
            >
              <v-icon
                name="hi-solid-lock-open"
                class="text-h-secondary-700 cursor-pointer hover:scale-125"
                @click="onChangeStatus(slotProps.data)"
              />
            </Button>
            <v-icon
              v-if="slotProps.data.status?.value == 'WAITING_ACTIVATION'"
              name="hi-solid-minus-circle"
              class="text-gray-400 cursor-default"
            />
          </div>
        </div>

        <div
          v-if="customActions"
          class="flex items-center justify-center flex-wrap gap-4"
        >
          <slot name="actions" :columnProps="slotProps"></slot>
        </div>
      </template>
    </Column>
  </DataTable>

  <div
    v-if="params?.total > params?.size"
    class="flex justify-end items-center"
  >
    <Paginator
      :rows="params?.size"
      :totalRecords="params?.total"
      @page="onPaginate($event)"
      :rowsPerPageOptions="[params?.size, 20, 30]"
    ></Paginator>
  </div>

  <OverlayPanel ref="filter" style="min-width: 200px">
    <section>
      <p class="mb-2 text-sm font-bold">Pesquisar por:</p>
      <div class="flex flex-row gap-2">
        <slot name="filters"></slot>
        <div class="w-100">
          <Button
            icon="pi pi-search"
            class="btn-primary"
            @click="onFilter($event)"
          />
        </div>
      </div>
    </section>
  </OverlayPanel>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";

export default defineComponent({
  name: "filterTable",
  emits: [
    "onEdit",
    "onChangeStatus",
    "onPaginate",
    "onToggleFilter",
    "onSortFilter",
    "onFilter",
    "onClearFilter",
    "onGoTo",
    "onSelect",
    "onTheme",
  ],
  props: {
    hasActions: {
      type: Boolean,
      default: true,
    },
    slotData: {
      type: Array as PropType<any>,
    },
    columns: {
      type: Array as PropType<any>,
    },
    actions: {
      type: Array as PropType<any>,
      default: [],
    },
    hasStatus: {
      type: Boolean,
      default: false,
    },
    params: {
      type: Object as PropType<any>,
      default: {},
    },
    loading: [Boolean],
    filters: {
      type: Object as PropType<any>,
      default: {},
    },
    hasFilter: {
      type: Boolean,
      default: true,
    },
    isOrdering: {
      type: Boolean,
      default: false,
    },
    actionsTitle: {
      type: String,
      default: "Ações",
    },
    customActions: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selected: this.checkbox as any,
    };
  },
  methods: {
    onEdit(event: any): void {
      this.$emit("onEdit", event);
    },
    onChangeStatus(event: any): void {
      this.$emit("onChangeStatus", event);
    },
    onGoTo(event: any): void {
      this.$emit("onGoTo", event);
    },
    onPaginate(event: any): void {
      this.$emit("onPaginate", event);
    },
    onFilter(event: any): void {
      (this.$refs["filter"] as any).hide();
      this.$emit("onFilter", event);
    },
    onClearFilter(): void {
      this.$emit("onClearFilter");
    },
    onToggleFilter(event: any, type: any): void {
      (this.$refs["filter"] as any).toggle(event);
      this.$emit("onToggleFilter", event, type.value);
    },
    onSortFilter(col: any): void {
      this.$emit("onSortFilter", col.value);
    },
    onEvent(event: any, type: string): void {
      if (type == "edit") this.onEdit(event);
      else if (type == "goTo") this.onGoTo(event);
      else if (type == "theme") this.$emit("onTheme", event);
    },
  },
});
</script>
